import { Bell, CheckCheck, ExternalLink } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useNotifications, Notification } from "@/hooks/useNotifications";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";

const NotificationBell = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [selectedNotif, setSelectedNotif] = useState<Notification | null>(null);

  const handleClick = (notif: Notification) => {
    if (!notif.is_read) markAsRead(notif.id);
    setSelectedNotif(notif);
    setPopoverOpen(false);
  };

  const handleLinkClick = () => {
    if (selectedNotif?.link) {
      navigate(selectedNotif.link);
      setSelectedNotif(null);
    }
  };

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button className="relative p-2 rounded-full hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
            <Bell className="h-5 w-5 text-muted-foreground" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 max-w-[calc(100vw-2rem)] overflow-hidden p-0">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={markAllAsRead}>
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </Button>
            )}
          </div>
          <ScrollArea className="max-h-80">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                No notifications yet
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications.slice(0, 10).map((notif) => (
                    <button
                      key={notif.id}
                      onClick={() => handleClick(notif)}
                      className={`block w-full min-w-0 overflow-hidden whitespace-normal text-left px-4 py-3 hover:bg-muted/50 transition-colors ${
                        !notif.is_read ? "bg-primary/5" : ""
                      }`}
                    >
                      <div className="flex w-full min-w-0 items-start gap-2 overflow-hidden">
                        {!notif.is_read && (
                          <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />
                        )}
                        <div className={`min-w-0 flex-1 overflow-hidden ${notif.is_read ? "pl-4" : ""}`}>
                          <p className="min-w-0 w-full whitespace-normal break-all text-sm font-medium text-foreground [overflow-wrap:anywhere]">
                            {notif.title}
                          </p>
                          {notif.message && (
                            <p className="mt-0.5 line-clamp-3 min-w-0 whitespace-normal break-all text-xs text-muted-foreground [overflow-wrap:anywhere]">
                              {notif.message}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[11px] text-muted-foreground/60">
                              {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                            </span>
                            {notif.link && <ExternalLink className="h-3 w-3 text-muted-foreground/40" />}
                          </div>
                        </div>
                      </div>
                    </button>
                ))}
              </div>
            )}
          </ScrollArea>
          {notifications.length > 0 && (
            <div className="border-t border-border px-4 py-2">
              <Link
                to="/notifications"
                onClick={() => setPopoverOpen(false)}
                className="block text-center text-xs font-medium text-primary hover:underline py-1"
              >
                View all notifications
              </Link>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Full notification detail dialog */}
      <Dialog open={!!selectedNotif} onOpenChange={(open) => !open && setSelectedNotif(null)}>
        <DialogContent className="max-w-[calc(100vw-2rem)] overflow-hidden pr-12 sm:max-w-md">
          <DialogHeader className="min-w-0">
            <DialogTitle className="max-w-full whitespace-normal break-all pr-2 text-base leading-snug [overflow-wrap:anywhere]">
              {selectedNotif?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="min-w-0 space-y-3 overflow-hidden">
            {selectedNotif?.message && (
              <p className="whitespace-pre-wrap break-all text-sm text-foreground [overflow-wrap:anywhere]">{selectedNotif.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              {selectedNotif && formatDistanceToNow(new Date(selectedNotif.created_at), { addSuffix: true })}
            </p>
            {selectedNotif?.link && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={handleLinkClick}>
                <ExternalLink className="h-3.5 w-3.5" />
                Go to link
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationBell;
